import { useEffect, useMemo } from 'react';

import { Container, Flex } from '@grupoboticario/flora-react';
import { MagazineIcon } from '@grupoboticario/flora-react-icons';

import { EmptyState, ErrorBoundaries, PageInfo, ResponsiveFloraTypography } from 'components';
import { ONBOARDINGS } from 'config/environments/shared';
import { StarlinkResponse } from 'domains/starlink';
import { getOnboarding } from 'helpers';
import { useInfinityStarlinkContent } from 'hooks';
import { newRelicMonitoringService } from 'services';

import {
  CarouselToShare,
  CarouselToShareSkeleton,
  CustomBanner,
  ModalOnboarding,
} from './components';
import Styles from './ImagesToShare.styles';

import type { ImagesToShareProps, ImagesToShareStarlinkType } from 'domains/images-to-share';

export const ImagesToShare = () => {
  const { showOnboardingSection } = getOnboarding(ONBOARDINGS.IMAGES_TO_SHARE);
  const {
    data: starlinkData,
    isLoading: isLoadingStarlinkContent,
    isSuccess: isSuccessStarlinkContent,
    isError: isErrorStarlinkContent,
  } = useInfinityStarlinkContent<ImagesToShareStarlinkType>({
    document: 'content_to_share',
    page_size: 50,
  });
  const starlinkContent = useMemo(
    () =>
      starlinkData
        ? (
            starlinkData?.pages
              .map((page) => page.items.map((item) => item))
              .flat() as unknown as Array<StarlinkResponse<unknown, ImagesToShareProps>>
          ).filter(({ data }) => {
            const { title, listToShare } = data as ImagesToShareProps;
            return title && listToShare && listToShare.length;
          })
        : [],
    [starlinkData]
  );

  useEffect(() => {
    if (!isLoadingStarlinkContent) {
      newRelicMonitoringService.addPageAction('pageview_imagens_divulgacao', {
        cd_detail: `${isSuccessStarlinkContent ? 'sucesso' : 'erro'}`,
      });
    }
  }, [isLoadingStarlinkContent, isSuccessStarlinkContent]);

  return (
    <Styles.Wrapper direction='column'>
      <Container size='large'>
        <PageInfo
          breadcrumbs={[
            {
              text: 'Imagens de divulgação',
              url: '',
            },
          ]}
        >
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography as='h1' fontSize='title' fontWeight='bold'>
              Imagens de divulgação
            </ResponsiveFloraTypography>

            <ResponsiveFloraTypography>
              Compartilhe conteúdos com seus clientes e aumente suas vendas
            </ResponsiveFloraTypography>
          </Flex>
        </PageInfo>
      </Container>

      <Styles.SectionWrapper>
        <Container size='large'>
          <CustomBanner />
        </Container>
      </Styles.SectionWrapper>

      <Styles.SectionWrapper>
        <Container size='large'>
          <Flex direction='column' gap='$6'>
            {isErrorStarlinkContent && (
              <ErrorBoundaries.ReloadPage
                title='Tivemos um problema para carregar a página'
                data-autotagging-flora-section='img_divulgacao'
              />
            )}

            {Boolean(starlinkContent.length) && isSuccessStarlinkContent && (
              <Styles.CarrouselsWrapper direction='column' gap='$6'>
                {showOnboardingSection && <ModalOnboarding />}
                {starlinkContent.map((item, index) => (
                  <CarouselToShare key={`carousel-${item.data.title}-${index}`} data={item.data} />
                ))}
              </Styles.CarrouselsWrapper>
            )}

            {isLoadingStarlinkContent && <CarouselToShareSkeleton />}

            {Boolean(!starlinkContent.length) && isSuccessStarlinkContent && (
              <Flex gap='$6' justify='center'>
                <EmptyState
                  title='Materiais em preparação.'
                  subtitle='Em breve, as imagens de divulgação estarão disponíveis aqui.'
                >
                  <MagazineIcon color='$backgroundAltPrimary' size={80} />
                </EmptyState>
              </Flex>
            )}
          </Flex>
        </Container>
      </Styles.SectionWrapper>
    </Styles.Wrapper>
  );
};
